import React, { useState, useEffect, useRef, useCallback, memo, useMemo, startTransition } from 'react';
import { Box, Typography, Alert, Fade, LinearProgress, alpha } from '@mui/material';
import BookingCalendarSkeleton, {
  BOOKING_CALENDAR_ROOM_ROWS_STORAGE_KEY,
  readStoredCalendarRoomRowCount,
} from './BookingCalendarSkeleton';
import BookingCalendarUltraView from './BookingCalendarUltraView';
import BookingCalendarFullViewImproved from './BookingCalendarFullViewImproved';
import BookingCalendarRoomViewImproved from './BookingCalendarRoomViewImproved';
import BookingCalendarGridView from './BookingCalendarGridView';
import BookingCalendarMonthOverview from './BookingCalendarMonthOverview';
import MobileBookingCalendarMonth from '../mobile/MobileBookingCalendarMonth';
import { getBookingCalendarData, getInventoryDashboardStatistics } from '../../services/api';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import {
  markDashboardDataLoaded,
  readDashboardDataRevision,
  readDashboardLoadedRevision,
  shouldRefreshDashboardData,
} from '../../utils/dashboardRefreshCoordinator';
import {
  DASHBOARD_CALENDAR_CACHE_TTL_MS,
  fetchWithDashboardCache,
} from '../../utils/dashboardDataCache';
import {
  readCachedCalendarPayload,
  resolveDashboardCalendarRequest,
} from '../../utils/dashboardPrefetch';
import { CALENDAR_GRID_DAY_COUNT } from '../../utils/dateUtils';
import {
  buildWorkspacePlaceholderCalendarData,
  CALENDAR_LOAD_FADE_MS,
  CALENDAR_VIEW_ENTER_MS,
  calendarContentRevealSx,
  calendarLoadingOverlaySx,
  calendarViewStackSx,
} from '../../utils/dashboardCalendarLoading';
import moment from 'moment';
import 'moment-timezone';
import './BookingCalendar.css';

/**
 * Main Booking Calendar Component
 * Handles data fetching and view switching
 */
const BookingCalendar = ({
  displayView: propDisplayView,
  currentDate: propCurrentDate,
  onViewChange: propOnViewChange,
  onDateChange: propOnDateChange,
  groupView = 'full',
  theme: propTheme,
  gridDayCount = CALENDAR_GRID_DAY_COUNT,
  onGridDayCountChange,
  onQuickBook,
  onWebCheckIn,
  onOpenWebCheckInInvite,
  onCheckout,
  onCheckIn,
  onEventBlock,
  onHousekeeping,
  onDirtyRoomHousekeeping,
  pendingCleaningByRoomId = {},
  dataRevision = 0,
  boardRefreshKey = 0,
  onBoardRefreshSettled,
  layoutVariant = 'desktop',
}) => {
  // Initialize with today's date
  const getTodayDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const resolveInitialCalendarState = () => {
    const initialView = propDisplayView !== undefined ? propDisplayView : 'ultra';
    const initialDate = propCurrentDate !== undefined ? propCurrentDate : getTodayDate();
    const needsRefresh = shouldRefreshDashboardData(readDashboardLoadedRevision());
    if (needsRefresh) {
      return {
        calendarData: null,
        loading: true,
      };
    }
    const cached = readCachedCalendarPayload(initialView, initialDate, gridDayCount);
    return {
      calendarData: cached,
      loading: !cached,
    };
  };

  const initialCalendarStateRef = useRef(null);
  if (initialCalendarStateRef.current === null) {
    initialCalendarStateRef.current = resolveInitialCalendarState();
  }
  const initialCalendarState = initialCalendarStateRef.current;

  const [calendarData, setCalendarData] = useState(initialCalendarState.calendarData);
  /** Used so week-grid skeleton row count matches hotel after first successful load. */
  const [lastKnownRoomRowCount, setLastKnownRoomRowCount] = useState(() => readStoredCalendarRoomRowCount());
  /** Map date -> inventory daily_stats row (same semantics as /inventory/ dashboard). */
  const [inventoryDailyByDate, setInventoryDailyByDate] = useState(null);
  /** First paint or view change before new payload (no progress UI — stale data kept when same view). */
  const [loading, setLoading] = useState(initialCalendarState.loading);
  const [error, setError] = useState(null);
  
  // Initialize theme synchronously
  const [internalTheme, setInternalTheme] = useState(() => detectThemeSync());

  // Use props if provided, otherwise use internal state
  const [internalDisplayView, setInternalDisplayView] = useState('ultra');
  const [internalCurrentDate, setInternalCurrentDate] = useState(getTodayDate());
  
  // Track expanded room types for room view - persists during date changes, resets on view change
  const [expandedRoomTypes, setExpandedRoomTypes] = useState(new Set());
  const prevDisplayViewForExpandedRef = useRef(null);
  
  const displayView = propDisplayView !== undefined ? propDisplayView : internalDisplayView;
  const currentDate = propCurrentDate !== undefined ? propCurrentDate : internalCurrentDate;

  /** Latest payload for fetchData (avoid stale closure / dependency loops). */
  const calendarDataRef = useRef(null);
  calendarDataRef.current = calendarData;
  /** Last displayView a successful fetch completed for (used vs. current view for loading vs. refresh). */
  const lastSuccessfulFetchViewRef = useRef(
    initialCalendarState.calendarData
      ? (propDisplayView !== undefined ? propDisplayView : 'ultra')
      : null,
  );
  const fetchGenerationRef = useRef(0);
  const appliedDataRevisionRef = useRef(readDashboardLoadedRevision());
  /** Force one network refetch whenever this calendar mounts (SPA route re-entry). */
  const mountRefreshPendingRef = useRef(true);
  const appliedBoardRefreshKeyRef = useRef(boardRefreshKey);
  const onBoardRefreshSettledRef = useRef(onBoardRefreshSettled);
  onBoardRefreshSettledRef.current = onBoardRefreshSettled;

  // Use prop theme if provided, otherwise use internal state
  const currentTheme = propTheme !== undefined ? propTheme : internalTheme;

  // Watch for theme changes only if theme prop is not provided
  useEffect(() => {
    if (propTheme === undefined) {
      const cleanup = watchThemeChanges((newTheme) => {
        setInternalTheme(newTheme);
      });

      return cleanup;
    }
  }, [propTheme]);

  /** Keep prior payload during view switches; placeholder shell covers mismatched shapes. */
  const prevDisplayViewRef = useRef(displayView);
  useEffect(() => {
    prevDisplayViewRef.current = displayView;
  }, [displayView]);

  const fetchData = useCallback(async ({
    silent = false,
    forceRefresh = false,
    revisionToApply = null,
    preserveRevision = false,
  } = {}) => {
    const fetchGeneration = fetchGenerationRef.current + 1;
    fetchGenerationRef.current = fetchGeneration;
    const requestedView = displayView;

    setError(null);
    if (!silent) {
      const sameViewAsLastOk =
        lastSuccessfulFetchViewRef.current === requestedView && Boolean(calendarDataRef.current);
      if (!sameViewAsLastOk) {
        const cachedPayload = readCachedCalendarPayload(requestedView, currentDate, gridDayCount);
        if (!cachedPayload) {
          setLoading(true);
        }
      }
    }

    try {
      const { startDate, endDate, viewType, cacheKey } = resolveDashboardCalendarRequest(
        requestedView,
        currentDate,
        gridDayCount,
      );

      const loadCalendar = () => fetchWithDashboardCache(
        cacheKey,
        () => getBookingCalendarData(startDate, endDate, viewType, { refresh: forceRefresh }),
        { ttl: DASHBOARD_CALENDAR_CACHE_TTL_MS, forceRefresh },
      );

      if (requestedView === 'month') {
        const [data, inv] = await Promise.all([
          loadCalendar(),
          getInventoryDashboardStatistics(startDate, endDate, null).catch(() => null),
        ]);
        if (fetchGeneration !== fetchGenerationRef.current || requestedView !== displayView) return;
        // Keep chrome/loading responsive while the dense board commits.
        startTransition(() => {
          setCalendarData(data);
          if (inv?.daily_stats) {
            const map = {};
            inv.daily_stats.forEach((row) => {
              if (row?.date) map[row.date] = row;
            });
            setInventoryDailyByDate(map);
          } else {
            setInventoryDailyByDate(null);
          }
        });
      } else {
        const data = await loadCalendar();
        if (fetchGeneration !== fetchGenerationRef.current || requestedView !== displayView) return;
        startTransition(() => {
          setCalendarData(data);
          setInventoryDailyByDate(null);
        });
      }

      lastSuccessfulFetchViewRef.current = requestedView;

      const currentRevision = readDashboardDataRevision();
      if (forceRefresh) {
        if (!preserveRevision) {
          const appliedRevision = revisionToApply ?? currentRevision;
          appliedDataRevisionRef.current = appliedRevision;
          markDashboardDataLoaded(appliedRevision);
        }
      } else if (dataRevision <= currentRevision) {
        appliedDataRevisionRef.current = currentRevision;
        markDashboardDataLoaded(currentRevision);
      }
    } catch (err) {
      if (fetchGeneration !== fetchGenerationRef.current) return;
      setError(err.message || 'Failed to load calendar data');
      setInventoryDailyByDate(null);
    } finally {
      if (fetchGeneration !== fetchGenerationRef.current) return;
      if (!silent) {
        setLoading(false);
      }
    }
  }, [currentDate, displayView, gridDayCount, dataRevision]);

  useEffect(() => {
    const mountRefresh = mountRefreshPendingRef.current;
    if (mountRefresh) {
      mountRefreshPendingRef.current = false;
    }

    const boardRefreshRequested = boardRefreshKey > appliedBoardRefreshKeyRef.current;
    if (boardRefreshRequested) {
      appliedBoardRefreshKeyRef.current = boardRefreshKey;
    }

    const needsFreshData = mountRefresh
      || boardRefreshRequested
      || shouldRefreshDashboardData(appliedDataRevisionRef.current)
      || dataRevision > appliedDataRevisionRef.current;

    if (needsFreshData) {
      if (!calendarDataRef.current) {
        setCalendarData(null);
        calendarDataRef.current = null;
        lastSuccessfulFetchViewRef.current = null;
      }
      // Keep existing cards visible while a route re-entry silent refresh runs.
      if (!calendarDataRef.current) {
        setLoading(true);
      }
    }

    let cancelled = false;
    (async () => {
      try {
        await fetchData({
          silent: Boolean(calendarDataRef.current),
          forceRefresh: needsFreshData,
          // Manual board refresh must not touch the shared dashboard revision
          // (that would also reload pending tasks).
          preserveRevision: boardRefreshRequested,
          revisionToApply: boardRefreshRequested
            ? null
            : (needsFreshData ? readDashboardDataRevision() : null),
        });
      } finally {
        if (boardRefreshRequested && !cancelled) {
          onBoardRefreshSettledRef.current?.();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dataRevision, fetchData, currentDate, displayView, gridDayCount, boardRefreshKey]);

  useEffect(() => {
    const n = calendarData?.rooms?.length;
    if (!n || typeof window === 'undefined') return;
    setLastKnownRoomRowCount(n);
    try {
      window.sessionStorage.setItem(BOOKING_CALENDAR_ROOM_ROWS_STORAGE_KEY, String(n));
    } catch {
      // ignore
    }
  }, [calendarData]);

  const handleViewChange = (view) => {
    // Reset expanded room types when view changes
    setExpandedRoomTypes(new Set());
    
    if (propOnViewChange) {
      propOnViewChange(view);
    } else {
      setInternalDisplayView(view);
      // Reset date to today when view changes
      const today = getTodayDate();
      setInternalCurrentDate(today);
    }
  };
  
  // Reset expanded room types when view changes (using useEffect to handle prop changes)
  useEffect(() => {
    if (prevDisplayViewForExpandedRef.current !== null && prevDisplayViewForExpandedRef.current !== displayView) {
      setExpandedRoomTypes(new Set());
    }
    prevDisplayViewForExpandedRef.current = displayView;
  }, [displayView]);

  const handleDateChange = (newDate) => {
    if (propOnDateChange) {
      propOnDateChange(newDate);
    } else {
      setInternalCurrentDate(newDate);
    }
  };

  const isWorkspaceLayout = layoutVariant === 'workspace';
  const isMobileLayout = layoutVariant === 'mobile';
  const usesInlineDataSkeleton = isWorkspaceLayout || isMobileLayout;
  const hasDataForCurrentView =
    Boolean(calendarData) && lastSuccessfulFetchViewRef.current === displayView;
  const isViewTransitioning =
    loading && lastSuccessfulFetchViewRef.current !== null && lastSuccessfulFetchViewRef.current !== displayView;
  const isInitialLoad = loading && !hasDataForCurrentView;
  const showLegacySkeleton = isInitialLoad && !usesInlineDataSkeleton;
  const placeholderData = useMemo(() => {
    if (!usesInlineDataSkeleton) return null;
    if (calendarData && !isViewTransitioning) return null;
    return buildWorkspacePlaceholderCalendarData({
      roomCount: lastKnownRoomRowCount,
      anchorDate: currentDate,
      gridDayCount,
      displayView,
    });
  }, [
    usesInlineDataSkeleton,
    calendarData,
    isViewTransitioning,
    lastKnownRoomRowCount,
    currentDate,
    gridDayCount,
    displayView,
  ]);
  const viewData = calendarData ?? placeholderData;
  const viewIsLoading = !calendarData && Boolean(placeholderData);
  const isSoftRefreshing = loading && hasDataForCurrentView;
  const inventoryForView = hasDataForCurrentView ? inventoryDailyByDate : null;
  const calendarRevealMs = viewIsLoading ? 0 : CALENDAR_LOAD_FADE_MS;

  return (
    <Box className="booking-calendar-container">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <br />
          Please refresh the page to try again
        </Alert>
      )}

      {showLegacySkeleton && (
        <BookingCalendarSkeleton
          theme={currentTheme}
          displayView={displayView}
          anchorDate={currentDate}
          groupView={groupView}
          roomRowCount={lastKnownRoomRowCount}
          layoutVariant={layoutVariant}
          gridDayCount={gridDayCount}
        />
      )}

      {!error && viewData && (() => {
        const calendarBody = (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            minHeight: layoutVariant === 'workspace' ? 0 : layoutVariant === 'mobile' ? 0 : 400,
            overflow: layoutVariant === 'workspace' || layoutVariant === 'mobile' ? 'visible' : 'hidden',
            ...calendarContentRevealSx(
              displayView !== 'ultra' && (viewIsLoading || isSoftRefreshing),
            ),
          }}
        >
          {isWorkspaceLayout && (viewIsLoading || isSoftRefreshing) ? (
            <LinearProgress
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 4,
                height: 2,
                borderRadius: 999,
                bgcolor: alpha('#ffffff', currentTheme === 'dark' ? 0.06 : 0.12),
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#4ce1b6',
                },
              }}
            />
          ) : null}
          {displayView !== 'ultra' && isSoftRefreshing ? (
            <Box sx={calendarLoadingOverlaySx(currentTheme === 'dark')} />
          ) : null}
          <Box
            key={displayView}
            sx={{
              ...calendarViewStackSx(),
              ...(layoutVariant === 'workspace'
                ? {}
                : {
                    animation: `fadeInUp ${CALENDAR_VIEW_ENTER_MS}ms ease-out`,
                    '@keyframes fadeInUp': {
                      '0%': { transform: 'translateY(6px)', opacity: 0.85 },
                      '100%': { transform: 'translateY(0)', opacity: 1 },
                    },
                  }),
            }}
          >
            {displayView === 'calendar' && (
              usesInlineDataSkeleton ? (
                <BookingCalendarGridView
                  data={viewData}
                  theme={currentTheme}
                  groupView={groupView}
                  onQuickBook={onQuickBook}
                  onCheckout={onCheckout}
                  onCheckIn={onCheckIn}
                  onOpenWebCheckInInvite={onOpenWebCheckInInvite}
                  layoutVariant={layoutVariant}
                  gridDayCount={gridDayCount}
                  onGridDayCountChange={onGridDayCountChange}
                  isLoading={viewIsLoading}
                />
              ) : (
              <Fade in timeout={CALENDAR_VIEW_ENTER_MS}>
                <Box>
                  <BookingCalendarGridView 
                    data={calendarData} 
                    theme={currentTheme}
                    groupView={groupView}
                    onQuickBook={onQuickBook}
                    onCheckout={onCheckout}
                    onCheckIn={onCheckIn}
                    onOpenWebCheckInInvite={onOpenWebCheckInInvite}
                    layoutVariant={layoutVariant}
                    gridDayCount={gridDayCount}
                    onGridDayCountChange={onGridDayCountChange}
                  />
                </Box>
              </Fade>
              )
            )}
            {displayView === 'month' && (
              usesInlineDataSkeleton ? (
                isMobileLayout ? (
                  <MobileBookingCalendarMonth
                    data={viewData}
                    inventoryByDate={inventoryForView}
                    anchorDate={currentDate}
                    onDateSelect={handleDateChange}
                  />
                ) : (
                  <BookingCalendarMonthOverview
                    data={viewData}
                    inventoryByDate={inventoryForView}
                    theme={currentTheme}
                    anchorDate={currentDate}
                    isLoading={viewIsLoading}
                  />
                )
              ) : (
              <Fade in timeout={CALENDAR_VIEW_ENTER_MS}>
                <Box>
                  {isMobileLayout ? (
                    <MobileBookingCalendarMonth
                      data={calendarData}
                      inventoryByDate={inventoryDailyByDate}
                      anchorDate={currentDate}
                      onDateSelect={handleDateChange}
                    />
                  ) : (
                    <BookingCalendarMonthOverview
                      data={calendarData}
                      inventoryByDate={inventoryDailyByDate}
                      theme={currentTheme}
                      anchorDate={currentDate}
                    />
                  )}
                </Box>
              </Fade>
              )
            )}
            {displayView === 'ultra' && (
              usesInlineDataSkeleton ? (
                <BookingCalendarUltraView 
                  data={viewData} 
                  groupView={groupView}
                  theme={currentTheme}
                  onQuickBook={onQuickBook}
                  onWebCheckIn={onWebCheckIn}
                  onOpenWebCheckInInvite={onOpenWebCheckInInvite}
                  onCheckout={onCheckout}
                  onCheckIn={onCheckIn}
                  onEventBlock={onEventBlock}
                  onHousekeeping={onHousekeeping}
                  onDirtyRoomHousekeeping={onDirtyRoomHousekeeping}
                  pendingCleaningByRoomId={pendingCleaningByRoomId}
                  layoutVariant={layoutVariant}
                  isLoading={viewIsLoading}
                />
              ) : (
              <Fade in timeout={CALENDAR_VIEW_ENTER_MS}>
                <Box>
                  <BookingCalendarUltraView 
                    data={calendarData} 
                    groupView={groupView}
                    theme={currentTheme}
                    onQuickBook={onQuickBook}
                    onWebCheckIn={onWebCheckIn}
                    onOpenWebCheckInInvite={onOpenWebCheckInInvite}
                    onCheckout={onCheckout}
                    onCheckIn={onCheckIn}
                    onEventBlock={onEventBlock}
                    onHousekeeping={onHousekeeping}
                    onDirtyRoomHousekeeping={onDirtyRoomHousekeeping}
                    pendingCleaningByRoomId={pendingCleaningByRoomId}
                    layoutVariant={layoutVariant}
                  />
                </Box>
              </Fade>
              )
            )}
            {displayView === 'full' && (
              <Fade in timeout={CALENDAR_VIEW_ENTER_MS}>
                <Box>
                  <BookingCalendarFullViewImproved 
                    data={calendarData} 
                    groupView={groupView}
                    theme={currentTheme}
                  />
                </Box>
              </Fade>
            )}
            {displayView === 'room' && (
              <Fade in timeout={CALENDAR_VIEW_ENTER_MS}>
                <Box>
                  <BookingCalendarRoomViewImproved 
                    data={calendarData} 
                    expandedRoomTypes={expandedRoomTypes}
                    onExpandedRoomTypesChange={setExpandedRoomTypes}
                    groupView={groupView}
                    theme={currentTheme}
                  />
                </Box>
              </Fade>
            )}
          </Box>
        </Box>
        );

        return isWorkspaceLayout ? calendarBody : (
          <Fade in timeout={calendarRevealMs}>
            {calendarBody}
          </Fade>
        );
      })()}

      {!loading && !error && !calendarData && !placeholderData && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No calendar data available
        </Typography>
      )}
    </Box>
  );
};

export default memo(BookingCalendar);

